import { MYNAVI_PRIMARY } from '@/components/Campaign/mynavi/Color'
import { MyNaviTypography } from '@/components/Campaign/mynavi/MyNaviUI/MyNaviTypography'
import { MyNaviWishButton } from '@/components/Campaign/mynavi/MyNaviWishButton/MyNaviWishButton'
import { IconCalendarWeek, IconChevronRight } from '@tabler/icons-react'

export type CollegeEvent = {
  eventId: number
  year: number
  /** 例: "7/7(火)" */
  date: string
  /** 例: "10:00〜12:00" | null */
  time: string | null
  /** 例: "千里山キャンパス" */
  campus: string
  /** イベント名。null・未指定の場合は非表示 */
  eventName?: string | null
  /** 開催情報。null・未指定の場合は非表示（時間の位置に表示） */
  holdingInfo?: string | null
  /** 獲得可能ポイント。null の場合は非表示 */
  point: number | null
  isWanted: boolean
  /** OC参加時に押印するスタンプID（null の場合は参加ボタンを非活性化） */
  stampId: number | null
}

export type MyNaviCollegeListProps = {
  collegeName: string
  /** 例: '私立大' | '公立大' | '短大' | '専門学校' */
  collegeType: string
  prefecture: string
  imageUrl?: string
  events: CollegeEvent[]
  totalEvents: number
  /** 「資料請求」を押下したとき。undefined の場合はボタンを disabled にする */
  onBrochureRequest?: () => void
  /** 「イベント情報」を押下したとき。undefined の場合はボタンを disabled にする */
  onEventInfo?: () => void
  onMoreSchedule: () => void
  onOcParticipateRequest: (stampId: number) => void
  onToggleWanted?: (eventId: number) => void
}

export const MyNaviCollegeList = ({
  collegeName,
  collegeType,
  prefecture,
  imageUrl,
  events,
  totalEvents,
  onBrochureRequest,
  onEventInfo,
  onMoreSchedule,
  onOcParticipateRequest,
  onToggleWanted,
}: MyNaviCollegeListProps) => {
  const displayedEvents = events.slice(0, 2)

  return (
    <div
      style={{
        backgroundColor: '#fbfbfb',
        padding: '12px 17px',
        borderBottom: '1px solid #d9d9d9',
      }}
    >
      {/* ヘッダー: 大学情報 + 画像 */}
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}
        onClick={onMoreSchedule}
        onKeyDown={(e) => e.key === 'Enter' && onMoreSchedule()}
      >
        <div style={{ flex: 1 }}>
          <MyNaviTypography variant="college-name" className="mb-1">
            {collegeName}
          </MyNaviTypography>
          <MyNaviTypography variant="tiny" as="p">
            {collegeType}　{prefecture}
          </MyNaviTypography>
        </div>
        <div>
          {imageUrl && (
            <img src={imageUrl} alt={collegeName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </div>
      </div>

      {/* 開催日程ボックス */}
      {totalEvents > 0 && (
        <div
          style={{
            border: '1px solid #058bbe',
            borderRadius: '5px',
            padding: '8px 12px',
            marginBottom: '8px',
          }}
        >
          {displayedEvents.map((event) =>
            event.time != null && event.time !== '' ? (
              /* time あり: 3行レイアウト（カレンダーアイコンなし） */
              <div key={event.eventId} style={{ padding: '6px 0' }}>
                <MyNaviTypography variant="micro" className="leading-none mb-1">
                  {event.year}年
                </MyNaviTypography>
                <div className="flex items-center justify-between gap-3">
                  <MyNaviTypography variant="caption" className="leading-none">
                    {event.date}
                  </MyNaviTypography>
                  <MyNaviWishButton isWished={event.isWanted} onClick={() => onToggleWanted?.(event.eventId)} />
                </div>
                <div className="flex items-center mt-2">
                  <MyNaviTypography variant="caption" className="flex-1 min-w-0 leading-none">
                    {event.campus}
                  </MyNaviTypography>
                  {/* 行きたいボタン（83px）の真下に右詰めで配置 */}
                  <div className="flex w-[83px] shrink-0 justify-end">
                    {event.point != null && (
                      <MyNaviTypography
                        variant="caption"
                        className="leading-none"
                        style={{ color: MYNAVI_PRIMARY, fontWeight: 700, textAlign: 'right' }}
                      >
                        {event.point.toLocaleString('ja-JP')}pt
                      </MyNaviTypography>
                    )}
                  </div>
                </div>
                {event.eventName && (
                  <MyNaviTypography variant="caption" as="p" className="mt-2 leading-none">
                    {event.eventName}
                  </MyNaviTypography>
                )}
                {event.holdingInfo && (
                  <MyNaviTypography variant="caption" as="p" className="mt-2 leading-none">
                    {event.holdingInfo}
                  </MyNaviTypography>
                )}
              </div>
            ) : (
              /* time なし: 1行レイアウト（カレンダーアイコンあり） */
              <div
                key={event.eventId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 0',
                }}
              >
                <IconCalendarWeek size={24} color="#686868" style={{ flexShrink: 0 }} />
                <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                  <MyNaviTypography variant="micro" className="leading-none">
                    {event.year}年
                  </MyNaviTypography>
                  <MyNaviTypography variant="caption" className="min-w-[50px]">
                    {event.date}
                  </MyNaviTypography>
                </div>
                <MyNaviTypography variant="caption" className="flex-1 min-w-0 self-end">
                  {event.campus}
                </MyNaviTypography>
                {/* 行きたいボタンと、その真下に右詰めのポイント */}
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <MyNaviWishButton isWished={event.isWanted} onClick={() => onToggleWanted?.(event.eventId)} />
                  {event.point != null && (
                    <MyNaviTypography
                      variant="caption"
                      className="leading-none"
                      style={{ color: MYNAVI_PRIMARY, fontWeight: 700, textAlign: 'right' }}
                    >
                      {event.point.toLocaleString('ja-JP')}pt
                    </MyNaviTypography>
                  )}
                </div>
              </div>
            ),
          )}

          {totalEvents > 2 && (
            <button
              type="button"
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                width: '100%',
                marginTop: '4px',
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={onMoreSchedule}
            >
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: '#686868',
                  fontFamily: "'Noto Sans JP', sans-serif",
                }}
              >
                別の日程を見る
              </span>
              <IconChevronRight size={18} color="#686868" />
            </button>
          )}
        </div>
      )}

      {/* ボタン行 */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="button"
          onClick={onBrochureRequest}
          disabled={!onBrochureRequest}
          style={{
            flex: 1,
            height: '36px',
            borderRadius: '5px',
            backgroundColor: '#e69f67',
            border: 'none',
            cursor: onBrochureRequest ? 'pointer' : 'default',
            color: 'white',
            fontSize: '12px',
            fontFamily: "'Noto Sans JP', sans-serif",
            opacity: onBrochureRequest ? 1 : 0.4,
          }}
        >
          資料請求
        </button>
        <button
          type="button"
          onClick={onEventInfo}
          disabled={!onEventInfo}
          style={{
            flex: 1,
            height: '36px',
            borderRadius: '5px',
            backgroundColor: '#73bdb0',
            border: 'none',
            cursor: onEventInfo ? 'pointer' : 'default',
            color: 'white',
            fontSize: '12px',
            fontFamily: "'Noto Sans JP', sans-serif",
            opacity: onEventInfo ? 1 : 0.4,
          }}
        >
          イベント情報
        </button>
      </div>
    </div>
  )
}
